import {Employee} from "../../api/Model";
import {useEmployeeHierarchyStore} from "../../api/EmployeeHierarchyStore";
import React, {useMemo} from "react";
import {
    ancestorsSequence,
    dfsSequence,
    filterHierarchy,
    HierarchyEmployee,
    SubtreeSize
} from "../../util/EmployeeHierarchy";
import {guessNeighbourhoods} from "./GuessNeighbourhoods";
import {Box, Paper, Stack, Typography} from "@mui/material";
import EmployeeAvatar from "../employee-avatar/EmployeeAvatar";
import {TeamPathBox} from "./TeamPathBox";
import {NeighbourhoodGuessPane, SelectedNeighbourhood} from "./NeighbourhoodGuessPane";
import {amber} from "@mui/material/colors";
import EmployeeLink from "../employee-link/EmployeeLink";
import {RemoteValue} from "../../util/RemoteValue";
import {EmployeeNeighbourhood} from "../../api/EmployeeNeighbourhoodStore";
import {Loading} from "../loading-data/LoadingData";
import {useResidenceStore} from "../../api/ResidenceStore";

export function NeighbourhoodGuessResultPane({employee, employeeNeighbourhoods, selectedNeighbourhood}: {
    employee: Employee,
    employeeNeighbourhoods: RemoteValue<EmployeeNeighbourhood>,
    selectedNeighbourhood?: SelectedNeighbourhood,
}) {
    const employeeHierarchy = useEmployeeHierarchyStore(employeeNeighbourhoods);
    const residencyModel = useResidenceStore().remoteValue;

    const guessData = useMemo(() => {
        return RemoteValue
            .combine({employeeHierarchy, residencyModel})
            .map(({employeeHierarchy: {globalHierarchy}, residencyModel}) => {
                // Build office-local hierarchy
                const localHierarchy = filterHierarchy(
                    globalHierarchy,
                    node => node.type === 'node' && residencyModel.isResident(node) && !node.deactivated,
                );

            // Find employee in the hierarchy
            let employeeNode: HierarchyEmployee | null = null;
            for (const node of dfsSequence(globalHierarchy)) {
                if (node.type === 'node' && node.id === employee.id) {
                    employeeNode = node;
                    break;
                }
            }

            const globalTeamSize = new SubtreeSize(globalHierarchy);
            const localTeamSize = new SubtreeSize(localHierarchy);


            const guesses = employeeNode ? (
                guessNeighbourhoods(employeeNode, globalTeamSize, localTeamSize)
            ) : ([]);


            return {
                employeeNode,
                guesses,
                globalTeamSize,
                localTeamSize,
            };
        });
    }, [employeeHierarchy, residencyModel, employee]);

    return (
        <Loading value={guessData}>{({employeeNode, guesses, globalTeamSize, localTeamSize}) => (
            employeeNode ? (
                <>
                    <Paper sx={{p: 2, mb: 2}}>
                        <Stack direction="column" spacing={1}>
                            {Array.from(ancestorsSequence(employeeNode)).map(node => node.type === 'node' && (
                                <Stack key={node.id} direction="row" alignItems="center" spacing={1}
                                       sx={{backgroundColor: node.id === employee.id ? amber['50'] : undefined}}>
                                    <EmployeeAvatar key={node.id} employee={node}/>
                                    <EmployeeLink employee={node} noWrap width="16em"/>
                                    <TeamPathBox teamPath={node.teamPath || []}/>
                                </Stack>
                            ))}
                        </Stack>
                    </Paper>
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Guessed Neighbourhoods:
                        </Typography>
                    </Box>
                    <Stack spacing={2}>
                        {guesses.map(guess => (
                            <NeighbourhoodGuessPane
                                key={guess.neighbourhood.id}
                                guess={guess}
                                globalTeamSize={globalTeamSize}
                                localTeamSize={localTeamSize}
                                selectedNeighbourhood={selectedNeighbourhood}
                            />
                        ))}
                    </Stack>
                </>
            ) : (
                <>Employee not found in the organization structure</>
            )
        )}</Loading>
    );
}
